import { useEffect, useRef, useState } from 'react';
import { Alert, Button, Input, Modal, Space, Table, Tag, Typography, message } from 'antd';
import dayjs from 'dayjs';
import { authFetch } from '../../utils/api';
import { useViewScope } from '../../utils/viewScope';
import {
  buildMemberDevicesPath,
  LatestRequestGuard,
  parseMemberUsername,
} from './memberDeviceQuery';
import {
  canConfirmMemberDeviceRevoke,
  canOpenMemberDeviceRevoke,
  canRevokeMemberDevice,
  isAlreadyRevokedResponse,
  isCurrentRevokeScope,
  MemberDeviceRevokeOperationGuard,
  memberDeviceRevokeRequest,
  reconcileMemberDeviceRevoke,
  type MemberDeviceRow,
  type RevokeResponse,
} from './memberDeviceRevoke';

const { Title, Paragraph, Text } = Typography;

const STATUS_COLORS: Record<MemberDeviceRow['status'], string> = {
  active: 'green',
  revoked: 'red',
  transferred: 'orange',
};

function formatTime(value: string | null): string {
  if (!value) return '-';
  const date = dayjs(value);
  return date.isValid() ? date.format('YYYY-MM-DD HH:mm:ss') : value;
}

/** UAT MVP: investigate historical device links for one formal member account. */
export function MemberDevicesPage() {
  const { scope, isAllScope } = useViewScope();
  const [input, setInput] = useState('');
  const [rows, setRows] = useState<MemberDeviceRow[]>([]);
  const [loading, setLoading] = useState(false);
  const [revokeSelection, setRevokeSelection] = useState<{ row: MemberDeviceRow; brand: string } | null>(null);
  const [revokeLoadingId, setRevokeLoadingId] = useState<number | null>(null);
  const requestGuard = useRef(new LatestRequestGuard());
  const revokeOperationGuard = useRef(new MemberDeviceRevokeOperationGuard());
  const scopeRef = useRef(scope);
  const rowsRef = useRef<MemberDeviceRow[]>([]);
  scopeRef.current = scope;

  useEffect(() => {
    // Scope is part of the result identity. This catches brand A → brand B as
    // well as aggregate transitions, and makes any earlier response stale.
    requestGuard.current.invalidate();
    revokeOperationGuard.current.invalidate();
    rowsRef.current = [];
    setRows([]);
    setLoading(false);
    setRevokeSelection(null);
    setRevokeLoadingId(null);
  }, [scope]);

  const username = parseMemberUsername(input);

  const lookup = async () => {
    if (isAllScope) return;
    if (!username) {
      message.warning('請輸入會員帳號');
      return;
    }
    const requestToken = requestGuard.current.begin();
    revokeOperationGuard.current.invalidate();
    setRevokeSelection(null);
    setRevokeLoadingId(null);
    setLoading(true);
    try {
      const response = await authFetch(buildMemberDevicesPath(username));
      const result = await response.json();
      if (!requestGuard.current.isCurrent(requestToken)) return;
      if (result.code === 0 && Array.isArray(result.data)) {
        rowsRef.current = result.data;
        setRows(result.data);
      } else {
        rowsRef.current = [];
        setRows([]);
        message.error(result.msg || '會員裝置關聯查詢失敗');
      }
    } catch {
      if (!requestGuard.current.isCurrent(requestToken)) return;
      rowsRef.current = [];
      setRows([]);
      message.error('會員裝置關聯查詢失敗');
    } finally {
      if (requestGuard.current.isCurrent(requestToken)) setLoading(false);
    }
  };

  const openRevokeModal = (row: MemberDeviceRow) => {
    if (!canOpenMemberDeviceRevoke(row, isAllScope, loading)) return;
    setRevokeSelection({ row, brand: scope });
  };

  const closeRevokeModal = () => {
    if (revokeLoadingId !== null) return;
    setRevokeSelection(null);
  };

  const confirmRevoke = async () => {
    const selection = revokeSelection;
    if (!selection || !canConfirmMemberDeviceRevoke(selection.row, revokeLoadingId)) return;
    if (!isCurrentRevokeScope(selection.brand, scopeRef.current)) {
      setRevokeSelection(null);
      message.warning('檢視品牌已變更，請重新查詢後再操作');
      return;
    }

    requestGuard.current.invalidate();
    setLoading(false);
    const revokeOperation = revokeOperationGuard.current.begin();
    setRevokeLoadingId(selection.row.authorizationId);
    try {
      const request = memberDeviceRevokeRequest(selection.row.authorizationId, selection.brand);
      const response = await authFetch(request.url, request.init);
      if (response.status === 401) return;
      const result: RevokeResponse = await response.json();
      if (!revokeOperationGuard.current.isCurrent(revokeOperation) || !isCurrentRevokeScope(selection.brand, scopeRef.current)) {
        if (revokeOperationGuard.current.isCurrent(revokeOperation)) message.warning('檢視品牌已變更，請重新查詢後再操作');
        return;
      }
      const reconciled = reconcileMemberDeviceRevoke(rowsRef.current, result, selection.row.authorizationId);
      if (!reconciled) {
        message.error('撤銷失敗，請重新查詢後再試');
        return;
      }
      rowsRef.current = reconciled;
      setRows(reconciled);
      message.success(isAlreadyRevokedResponse(result) ? '此裝置授權已是撤銷狀態' : '裝置授權已撤銷');
    } catch {
      if (revokeOperationGuard.current.isCurrent(revokeOperation) && isCurrentRevokeScope(selection.brand, scopeRef.current)) {
        message.error('撤銷失敗，請重新查詢後再試');
      }
    } finally {
      if (revokeOperationGuard.current.isCurrent(revokeOperation)) {
        setRevokeLoadingId(null);
        setRevokeSelection(null);
      }
    }
  };

  return (
    <div style={{ padding: 24 }}>
      <Title level={3}>會員裝置關聯</Title>
      <Paragraph type="secondary">
        以會員帳號查詢該品牌下曾關聯過的所有裝置。裝置識別碼依資安規範遮罩顯示。
      </Paragraph>
      {isAllScope ? (
        <Alert
          type="info"
          showIcon
          message="請先在上方選擇單一品牌"
          description="此查詢會以目前檢視品牌執行，避免跨品牌揭露會員裝置關聯。"
          style={{ marginBottom: 16 }}
        />
      ) : null}
      <Space style={{ marginBottom: 16 }} wrap>
        <Input
          aria-label="帳號"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          onPressEnter={lookup}
          placeholder="會員帳號，例如 bo-user"
          style={{ width: 280 }}
          disabled={isAllScope}
        />
        <Button
          type="primary"
          onClick={lookup}
          disabled={isAllScope || !username}
          loading={loading}
        >
          查詢
        </Button>
      </Space>
      <Table<MemberDeviceRow>
        dataSource={rows}
        loading={loading}
        rowKey="authorizationId"
        data-testid="member-devices-table"
        pagination={false}
        locale={{ emptyText: '尚未查詢，或此會員在目前品牌沒有裝置關聯紀錄' }}
        scroll={{ x: 1170 }}
      >
        <Table.Column<MemberDeviceRow>
          dataIndex="username"
          title="帳號"
          width={180}
          render={(value: string) => <Text code>{value}</Text>}
        />
        <Table.Column<MemberDeviceRow>
          dataIndex="deviceId"
          title="裝置 ID（遮罩）"
          width={180}
          render={(value: string) => <Text code>{value}</Text>}
        />
        <Table.Column<MemberDeviceRow>
          dataIndex="linkedAt"
          title="關聯時間"
          width={180}
          render={formatTime}
        />
        <Table.Column<MemberDeviceRow>
          dataIndex="lastSeenAt"
          title="最後活動"
          width={180}
          render={formatTime}
        />
        <Table.Column<MemberDeviceRow>
          dataIndex="status"
          title="狀態"
          width={120}
          render={(status: MemberDeviceRow['status']) => (
            <Tag color={STATUS_COLORS[status] ?? 'default'}>{status}</Tag>
          )}
        />
        <Table.Column<MemberDeviceRow>
          dataIndex="revokedReason"
          title="撤銷原因"
          width={160}
          render={(value: string | null) => value ?? '-'}
        />
        <Table.Column<MemberDeviceRow>
          title="操作"
          width={120}
          fixed="right"
          render={(_, row) => canRevokeMemberDevice(row, isAllScope) ? (
            <Button danger size="small" onClick={() => openRevokeModal(row)}
              loading={revokeLoadingId === row.authorizationId}
              disabled={loading || revokeLoadingId !== null}>
              Revoke
            </Button>
          ) : '-'}
        />
      </Table>
      <Modal open={revokeSelection !== null} title="確認撤銷裝置授權"
        okText="確認 Revoke" okButtonProps={{ danger: true, loading: revokeLoadingId !== null }}
        cancelText="取消" cancelButtonProps={{ disabled: revokeLoadingId !== null }}
        maskClosable={revokeLoadingId === null} closable={revokeLoadingId === null}
        keyboard={revokeLoadingId === null} onCancel={closeRevokeModal} onOk={confirmRevoke}>
        {revokeSelection ? <Space direction="vertical" size="middle">
          <Text>會員：<Text code>{revokeSelection.row.username}</Text></Text>
          <Text>裝置 ID（遮罩）：<Text code>{revokeSelection.row.deviceId}</Text></Text>
          <Text>品牌：<Text code>{revokeSelection.brand}</Text></Text>
          <Text type="danger">確認後會立即撤銷此裝置授權，使其會員 session、refresh 與快速登入憑證、connect ticket 失效，並要求關閉現有 VPN 連線。</Text>
          <Text type="secondary">此操作不會永久封鎖會員日後重新登入或重新授權裝置。</Text>
        </Space> : null}
      </Modal>
    </div>
  );
}
